/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Injectable} from '@angular/core';
import {CompoApiService} from './compo-api.service';
import {Observable} from 'rxjs';
import {ApiResponse} from '../interfaces/ApiResponse';
import {OrderStatus} from '../interfaces/OrderStatus';
import {catchError} from 'rxjs/operators';
import {BaseService} from './base.service';
import {Order} from '../interfaces/Order';
import { HttpParams } from '@angular/common/http';
import {PageApiResponse} from '../interfaces/PageApiResponse';
import {CartProduct} from '../interfaces/CartProduct';
import {OrderForm} from '../interfaces/OrderForm';
import {OrderInfo} from '../interfaces/OrderInfo';
import {CustomerGroup} from '../interfaces/CustomerGroup';
import {PickupAddress} from '../interfaces/PickupAddress';
import {DeliveryAddress} from '../interfaces/DeliveryAddress';

@Injectable({
  providedIn: 'root'
})
export class OrderService extends BaseService {

  constructor(private readonly apiService: CompoApiService) {
    super();
  }

  get(id: number): Observable<ApiResponse<Order>> {
    return this.apiService.get<Order>('/order/' + id).pipe(catchError(err => this.handleError(err)));
  }

  getStatuses(): Observable<ApiResponse<OrderStatus[]>> {
    return this.apiService.get<OrderStatus[]>('/order-status').pipe(catchError(err => this.handleError(err)));
  }

  filter(params: HttpParams): Observable<ApiResponse<PageApiResponse<Order[]>>> {
    return this.apiService.getWithParams<PageApiResponse<Order[]>>('/order/filter', params).pipe(catchError(err => this.handleError(err)));
  }

  copyToCart(order: Order): Observable<ApiResponse<CartProduct[]>> {
    return this.apiService.get<CartProduct[]>('/order/' + order.id + '/to-cart').pipe(catchError(err => this.handleError(err)));
  }

  create(orderForm: OrderForm): Observable<ApiResponse<OrderInfo>> {
    return this.apiService.post<OrderForm, OrderInfo>('/order', orderForm).pipe(catchError(err => this.handleError(err)));
  }

  export(order: Order): Observable<Blob> {
    return this.apiService.downloadUrl('/order/' + order.id + '/export').pipe(catchError(err => this.handleError(err)));
  }

  reserve(order: Order): Observable<ApiResponse<Date>> {
    return this.apiService.get<Date>('/order/' + order.id + '/reserve').pipe(catchError(err => this.handleError(err)));
  }

  getPickupAddresses(customerGroup: CustomerGroup): Observable<ApiResponse<PickupAddress[]>> {
    return this.apiService.get<PickupAddress[]>('/customer-group/' + customerGroup.id + '/pickup-addresses').
      pipe(catchError(err => this.handleError(err)));
  }

  getDeliveryAddresses(): Observable<ApiResponse<DeliveryAddress[]>> {
    return this.apiService.get<DeliveryAddress[]>('/delivery-address').
      pipe(catchError(err => this.handleError(err)));
  }

  updateComment(order: Order): Observable<any> {
    return this.apiService.patch<string, any>('/order/' + order.id + '/update-comment', order.comment).pipe(catchError(err => this.handleError(err)));
  }

  downloadCertificates(order: Order): Observable<Blob> {
    return this.apiService.downloadUrl('/order/' + order.id + '/download-certificates').pipe(catchError(err => this.handleError(err)));
  }
}
